import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../components/Hero';
import Manifesto from '../components/Manifesto';
import Arsenal from '../components/Arsenal';
import KillCount from '../components/KillCount';
import Contact from '../components/Contact';

const Home: React.FC = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "DisruptNext",
    "alternateName": "DisruptNext Digital Marketing Agency Kerala",
    "description": "Kerala's most aggressive digital marketing agency based in Kochi. Specializing in SEO, PPC advertising, social media marketing, and conversion optimization for businesses across all 14 districts of Kerala including Ernakulam, Trivandrum, Kozhikode, Thrissur, and more.",
    "url": "https://disruptnext.com",
    "telephone": "+91-XXXX-XXX-XXX",
    "email": "hello@disruptnext.com",
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Seaport Airport Road, Thrikkakara",
      "addressLocality": "Kochi",
      "addressRegion": "Kerala",
      "postalCode": "682021",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "10.0159",
      "longitude": "76.3419"
    },
    "areaServed": [
      { "@type": "City", "name": "Kochi" },
      { "@type": "City", "name": "Ernakulam" },
      { "@type": "City", "name": "Thiruvananthapuram" },
      { "@type": "City", "name": "Kozhikode" },
      { "@type": "City", "name": "Thrissur" },
      { "@type": "City", "name": "Kottayam" },
      { "@type": "City", "name": "Palakkad" },
      { "@type": "City", "name": "Malappuram" },
      { "@type": "City", "name": "Kannur" },
      { "@type": "City", "name": "Kasaragod" },
      { "@type": "City", "name": "Wayanad" },
      { "@type": "City", "name": "Idukki" },
      { "@type": "City", "name": "Alappuzha" },
      { "@type": "City", "name": "Pathanamthitta" },
      { "@type": "City", "name": "Kollam" },
      { "@type": "State", "name": "Kerala" }
    ],
    "serviceType": [
      "Digital Marketing",
      "SEO Services",
      "PPC Advertising",
      "Social Media Marketing",
      "Content Marketing",
      "Conversion Rate Optimization",
      "Website Development"
    ],
    "priceRange": "$$$",
    "openingHours": "Mo-Fr 09:00-18:00",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer, UPI",
    "currenciesAccepted": "INR",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Digital Marketing Services Kerala",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Guerrilla PPC",
            "description": "Aggressive pay-per-click campaigns that dominate search results for Kerala businesses"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Viral Contagion",
            "description": "Viral content strategies for social media targeting Kerala audiences"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SEO Warfare",
            "description": "Search engine optimization tactics for ranking dominance in Kerala markets"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Conversion Assault",
            "description": "Conversion rate optimization for maximum ROI on Kerala digital campaigns"
          }
        }
      ]
    }
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "Service",
        "name": "Guerrilla PPC",
        "description": "Aggressive pay-per-click campaigns that dominate search results. Google Ads, Meta Ads, LinkedIn Ads management with ruthless optimization for Kerala businesses.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "DisruptNext"
        },
        "serviceType": "Pay-Per-Click Advertising",
        "areaServed": [
          { "@type": "State", "name": "Kerala" },
          { "@type": "City", "name": "Kochi" },
          { "@type": "City", "name": "Ernakulam" }
        ]
      },
      {
        "@type": "Service",
        "name": "Viral Contagion",
        "description": "Viral content strategies for social media that spread like wildfire. TikTok, Instagram, X/Twitter campaigns designed to explode in the Kerala market.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "DisruptNext"
        },
        "serviceType": "Social Media Marketing",
        "areaServed": [
          { "@type": "State", "name": "Kerala" },
          { "@type": "City", "name": "Kochi" },
          { "@type": "City", "name": "Ernakulam" }
        ]
      },
      {
        "@type": "Service",
        "name": "SEO Warfare",
        "description": "Search engine optimization tactics for ranking dominance in Kerala. Technical SEO, content strategy, and link building that crushes local competition.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "DisruptNext"
        },
        "serviceType": "Search Engine Optimization",
        "areaServed": [
          { "@type": "State", "name": "Kerala" },
          { "@type": "City", "name": "Kochi" },
          { "@type": "City", "name": "Ernakulam" }
        ]
      },
      {
        "@type": "Service",
        "name": "Conversion Assault",
        "description": "Conversion rate optimization for maximum ROI on Kerala digital campaigns. A/B testing, funnel optimization, and landing page design that converts visitors into customers.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "DisruptNext"
        },
        "serviceType": "Conversion Rate Optimization",
        "areaServed": [
          { "@type": "State", "name": "Kerala" },
          { "@type": "City", "name": "Kochi" },
          { "@type": "City", "name": "Ernakulam" }
        ]
      }
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "DisruptNext",
    "url": "https://disruptnext.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://disruptnext.com/#/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="relative z-10">
      <Helmet>
        <title>Digital Marketing Agency Kerala | DisruptNext | Kochi, Ernakulam</title>
        <meta name="description" content="Kerala's most aggressive digital marketing agency based in Kochi. SEO, PPC, social media & conversion optimization for businesses across Ernakulam, Trivandrum, Kozhikode & all Kerala. We don't play nice. We win." />
        <meta property="og:title" content="Digital Marketing Agency Kerala | DisruptNext | Kochi, Ernakulam" />
        <meta property="og:description" content="Kerala's most aggressive digital marketing agency. Based in Kochi, serving all 14 districts. SEO, PPC, social media & conversion optimization. We don't play nice. We win." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://disruptnext.com" />
        <meta property="og:image" content="https://disruptnext.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DisruptNext | Digital Marketing Agency" />
        <meta name="twitter:description" content="We don't play nice. We win." />
        <meta name="twitter:image" content="https://disruptnext.com/og-image.jpg" />
        <link rel="canonical" href="https://disruptnext.com" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(servicesSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>
      <Hero />
      <Manifesto />
      <Arsenal />
      <KillCount />
      <Contact />
    </div>
  );
};

export default Home;